import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Panel, ProgressBar, Skeleton, Text } from '../../../../components';
import { useThemeMode } from '../../../../theme/OwsTheme';
import { CUST_GEN_DOT_FILL, CUST_GEN_DOT_FILL_LIGHT } from '../../../../theme/customizer-tokens';
import { DotField } from '../../../customizer/components/DotField';
import { GenerateButton } from '../../../creatives/components/GenerateButton';
import { LumioMark } from '../../../creatives/components/LumioMark';
import type { LookSample, SiteLook, SiteRegenState } from '../../art-style-api';
import { lookLabel, lookStatusCopy, pickLookTiles, ratioLabel } from '../../site-looks';
import { siteActivityCopy } from '../../site-regen';
import {
  Header,
  NameBlock,
  EyebrowRow,
  Eyebrow,
  Tag,
  MatureTag,
  Name,
  Status,
  Actions,
  PrimaryBadge,
  PrimaryDot,
  PreviewLink,
  Body,
  SectionHead,
  SectionTitle,
  SectionNote,
  Progress,
  ProgressCopy,
  Tiles,
  Tile,
  Media,
  Img,
  Placeholder,
  PlaceholderText,
  Overlay,
  Backdrop,
  Foot,
  TileName,
  TileMeta,
  Empty,
  EmptyCopy,
  EmptyTitle,
  EmptyText,
  Shot,
  ShotFrame,
  ShotImg,
  ShotCaption,
} from './SiteLookView.styles';

export type SiteLookViewProps = {
  look: SiteLook;
  /** The website shows this look now. */
  primary: boolean;
  /** Its art is being drawn (the site job's `look_draw` on it). */
  drawing: boolean;
  /** The site is being switched to it (the site job's `look_apply` on it). */
  switching: boolean;
  /** The brand's site job (its progress shows while this look is drawn). */
  job: SiteRegenState | null;
  /** The brand's own art in this look; undefined until read. */
  samples: LookSample[] | undefined;
  /** A site job runs or a site request is on its way: the actions wait. */
  locked: boolean;
  /** The brand's site in this look before it is switched ("" when not known, or nothing to preview). */
  previewUrl: string;
  /** Credits drawing the site's art in this look costs, shown on "Generate art in this style" (undefined until the
   *  price table loads: no price rather than a guess). */
  cost?: number;
  /** "Generate art in this style" (asks first). */
  onGenerate: () => void;
  /** "Set as primary" (asks first). */
  onSetPrimary: () => void;
};

/** The ratios the empty preview stands in for (Website Creatives' 2:1 hero, 1:1 tile, 1:2 portrait, 3:1 strip). */
const PLACEHOLDER_RATIOS: readonly number[] = [2, 1, 0.5, 3];

/** One tile of the brand's art in the look, fading in once decoded. */
const SampleTile = ({ sample }: { sample: LookSample }): React.ReactElement => {
  const [loaded, setLoaded] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);
  // A cached image can finish decoding before onLoad attaches.
  React.useEffect(() => {
    setLoaded(Boolean(imgRef.current?.complete));
  }, [sample.url]);
  return (
    <Tile $aspect={sample.aspect}>
      <Media $aspect={sample.aspect}>
        <Img
          ref={imgRef}
          src={sample.url}
          alt={sample.name}
          loading="lazy"
          decoding="async"
          $loaded={loaded}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
        />
      </Media>
      <Foot>
        {sample.name ? <TileName raw text={sample.name} tag="span" /> : null}
        <TileMeta raw text={ratioLabel(sample.aspect)} tag="span" />
      </Foot>
    </Tile>
  );
};

/**
 * A catalog look, opened from the strip: read-only (it can't be edited, duplicated or deleted). Its header names it,
 * says where it stands and offers its one action: "Set as primary" once its art is drawn (the website switches to it),
 * "Generate art in this style" while the brand has none or only some (drawn next to the current look), nothing while
 * the website shows it. Below, the brand's own art in it in the ratios Website Creatives uses; while it is drawn, the
 * job's progress; before any art exists, placeholders, a screenshot of a site of the brand's design in it, and the
 * Generate trigger.
 */
export const SiteLookView = ({
  look,
  primary,
  drawing,
  switching,
  job,
  samples,
  locked,
  previewUrl,
  cost,
  onGenerate,
  onSetPrimary,
}: SiteLookViewProps): React.ReactElement => {
  const { t } = useTranslation();
  const dotFill = useThemeMode() === 'light' ? CUST_GEN_DOT_FILL_LIGHT : CUST_GEN_DOT_FILL;
  const label = lookLabel(look.name) || t('Untitled look');
  const tiles = React.useMemo(() => pickLookTiles(samples ?? []), [samples]);
  const canGenerate = !primary && !drawing && look.art !== 'ready';
  const canSetPrimary = !primary && !drawing && !switching && look.art === 'ready';

  let action: React.ReactNode = null;
  if (primary) {
    action = (
      <PrimaryBadge>
        <PrimaryDot aria-hidden="true" />
        <Text text="Primary" tag="span" />
      </PrimaryBadge>
    );
  } else if (canSetPrimary) {
    action = (
      <>
        {previewUrl ? (
          <PreviewLink
            as="a"
            href={previewUrl}
            target="_blank"
            rel="noopener noreferrer"
            $variant="outline"
            $fullWidth={false}
            $iconOnly={false}
          >
            <Icon name="external-link" size={18} />
            <Text text="Preview on your site" tag="span" />
          </PreviewLink>
        ) : null}
        <Button
          label={t('Set as primary')}
          icon="globe"
          disabled={locked}
          onClick={() => {
            if (!locked) onSetPrimary();
          }}
        />
      </>
    );
  }

  // Generate lives in the body only (never in the header): the empty state carries it before any art exists, and a
  // partly drawn look shows it above the art it has.
  const generate = canGenerate ? (
    <GenerateButton
      compact
      label={t('Generate art in this style')}
      cost={cost}
      disabled={locked}
      onClick={() => {
        if (!locked) onGenerate();
      }}
    />
  ) : null;

  const progress =
    drawing && job ? (
      <Progress role="status" aria-live="polite">
        <ProgressCopy text={siteActivityCopy(job, t)} tag="span" />
        {job.steps_total > 0 ? (
          <ProgressBar value={Math.min(100, (job.steps_done / job.steps_total) * 100)} animate />
        ) : null}
      </Progress>
    ) : null;

  let body: React.ReactNode;
  if (drawing) {
    body = (
      <Tiles>
        {PLACEHOLDER_RATIOS.map((ratio, index) => (
          <Tile key={ratio} $aspect={ratio}>
            <Media $aspect={ratio}>
              <Overlay>
                <Backdrop />
                <DotField seed={index + 1} fill={dotFill} />
              </Overlay>
            </Media>
            <Foot>
              <TileMeta raw text={ratioLabel(ratio)} tag="span" />
            </Foot>
          </Tile>
        ))}
      </Tiles>
    );
  } else if (samples === undefined) {
    body = (
      <Tiles aria-busy="true">
        {PLACEHOLDER_RATIOS.map((ratio) => (
          <Tile key={ratio} $aspect={ratio}>
            <Media $aspect={ratio}>
              <Skeleton block height="100%" radius={0} />
            </Media>
          </Tile>
        ))}
      </Tiles>
    );
  } else if (tiles.length > 0) {
    body = (
      <>
        {generate ? <Actions>{generate}</Actions> : null}
        <Tiles>
          {tiles.map((sample) => (
            <SampleTile key={`${sample.deployment_uuid}:${sample.slot}:${sample.url}`} sample={sample} />
          ))}
        </Tiles>
      </>
    );
  } else {
    // No art to show: the screenshot of a site of the brand's design in this look first, then (before any art exists)
    // what Generate does and its trigger, then the stand-ins.
    body = (
      <>
        {look.screenshot_url ? (
          <Shot>
            <ShotFrame>
              <ShotImg src={look.screenshot_url} alt={t('A site in {{look}}', { look: label })} loading="lazy" />
            </ShotFrame>
            <ShotCaption text="A site with your design in this look (not your own content)." tag="span" />
          </Shot>
        ) : null}
        {look.art === 'none' ? (
          <Empty>
            <LumioMark size={28} />
            <EmptyCopy>
              <EmptyTitle text="Your website has no art in this look yet" tag="span" />
              <EmptyText
                text="Lumio can draw your welcome bonus, hero, promotion and gamification art in it. Players keep seeing your current look until you set it as primary."
                tag="span"
              />
            </EmptyCopy>
            {generate}
          </Empty>
        ) : null}
        <Tiles>
          {PLACEHOLDER_RATIOS.map((ratio) => (
            <Tile key={ratio} $aspect={ratio}>
              <Media $aspect={ratio}>
                <Placeholder>
                  <Icon name="grid" size={18} />
                  <PlaceholderText text="Not drawn yet" tag="span" />
                </Placeholder>
              </Media>
              <Foot>
                <TileMeta raw text={ratioLabel(ratio)} tag="span" />
              </Foot>
            </Tile>
          ))}
        </Tiles>
      </>
    );
  }

  return (
    <Panel>
      <Header>
        <NameBlock>
          <EyebrowRow>
            <Eyebrow text="Website look" tag="span" />
            {look.original ? (
              <Tag>
                <Text text="Original" tag="span" />
              </Tag>
            ) : null}
            {look.mature ? (
              <MatureTag title={t('Mature content')}>
                <Text text="18+" tag="span" />
              </MatureTag>
            ) : null}
          </EyebrowRow>
          <Name text={label} raw tag="h2" />
          <Status text={lookStatusCopy(look, { primary, drawing, switching }, t)} tag="paragraph" />
        </NameBlock>
        {action ? <Actions>{action}</Actions> : null}
      </Header>
      <Body>
        <SectionHead>
          <SectionTitle text="Your art in this look" tag="span" />
          <SectionNote text="From your own website, in the formats Website Creatives uses." tag="span" />
        </SectionHead>
        {progress}
        {body}
      </Body>
    </Panel>
  );
};

export default SiteLookView;
